import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { rawInitData } from "../platform/telegram";

/**
 * دسترسی کاربر (رایگان یا پلاس) — **همیشه از سرور**.
 *
 * اگر اپ خودش تصمیم بگیرد چه کسی پلاس است، نسخهٔ دست‌کاری‌شده ظرف یک هفته در
 * کانال‌ها پخش می‌شود. پس اینجا فقط چیزی که سرور گفته نگه داشته می‌شود، و تا
 * وقتی سرور جواب نداده، پیش‌فرض «رایگان» است — نه «پلاس».
 *
 * سقف‌ها هم از سرور می‌آیند: عوض کردن مرز رایگان/پولی نباید بیلد جدید بخواهد.
 */

export interface Entitlement {
  plus: boolean;
  plan: string;
  until: number | null;
  limits: { habits: number; activeTasks: number; historyDays: number; freezeTokens: number };
  /** آیا اصلاً به سروری وصلیم؟ در حالت محلی، سقفی اعمال نمی‌شود. */
  online: boolean;
}

const FREE: Entitlement = {
  plus: false,
  plan: "free",
  until: null,
  limits: { habits: 5, activeTasks: 50, historyDays: 30, freezeTokens: 1 },
  online: false,
};

const Ctx = createContext<Entitlement>(FREE);

export function EntitlementProvider({ children }: { children: React.ReactNode }) {
  const [ent, setEnt] = useState<Entitlement>(FREE);

  useEffect(() => {
    const initData = rawInitData();
    // localhost هم امن حساب می‌شود تا توسعه و تست واقعاً قابل انجام باشد
    const secure = location.protocol === "https:" || location.hostname === "localhost";
    if (!initData || !secure) return; // حالت محلی
    let alive = true;

    const load = async () => {
      try {
        const r = await fetch(`${location.origin}/api/me`, {
          headers: { "X-Telegram-Init-Data": initData },
        });
        if (!r.ok) return;
        const out = (await r.json()) as { entitlement?: Omit<Entitlement, "online"> };
        if (alive && out.entitlement) setEnt({ ...out.entitlement, online: true });
      } catch {
        // آفلاین — همان رایگان می‌ماند و چیزی نمی‌شکند
      }
    };

    void load();
    // بعد از برگشتن از درگاه، کاربر دوباره اپ را باز می‌کند؛ همان‌جا تازه شود
    document.addEventListener("visibilitychange", load);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", load);
    };
  }, []);

  return <Ctx.Provider value={ent}>{children}</Ctx.Provider>;
}

export const useEntitlement = (): Entitlement => useContext(Ctx);

/**
 * آیا این عدد به سقف خورده است؟
 * در حالت محلی (بدون سرور) هیچ سقفی اعمال نمی‌شود — کسی که آفلاین کار می‌کند
 * نباید با پیام «اشتراک بخر» روبه‌رو شود.
 */
export function useAtLimit(kind: "habits" | "activeTasks", count: number): boolean {
  const ent = useEntitlement();
  return useMemo(() => {
    if (!ent.online || ent.plus) return false;
    const cap = ent.limits[kind];
    return Number.isFinite(cap) && count >= cap;
  }, [ent, kind, count]);
}
